/**
 * NeuralFace — SFUI-002 P0 · geometric wireframe persona at hero center.
 *
 * Pure SVG. No images. Geometric face outline + circuit traces extending
 * out from temples/jaw/eyes. Breathing nodes at intersections. Energy
 * pulses traveling along every path (cyan primary + magenta secondary).
 * Responsive (200px mobile → 400px desktop). Mount above hero title.
 */
import React from "react";

// Paths defined in the 100×100 viewBox · pulses ride these via animateMotion
const FACE_PATHS = [
  // Face outline (hexagon-ish)
  { id: "fo",  d: "M 50,12 L 72,22 L 80,46 L 72,72 L 50,86 L 28,72 L 20,46 L 28,22 Z", w: 1.4 },
  // Eyes
  { id: "le",  d: "M 36,40 A 4,3 0 1 0 36.001,40", w: 1.2 },
  { id: "re",  d: "M 64,40 A 4,3 0 1 0 64.001,40", w: 1.2 },
  // Nose ridge
  { id: "no",  d: "M 50,42 L 50,58", w: 1.0 },
  // Mouth
  { id: "mo",  d: "M 42,68 Q 50,72 58,68", w: 1.0 },
];

// External circuit traces (the "neural" wiring extending from the face)
const CIRCUIT_PATHS = [
  // Left temple → out
  { id: "c1", d: "M 28,22 L 14,18 L 6,28 L 4,46",                    dur: 4.2, color: "cyan"    },
  // Left jaw → out
  { id: "c2", d: "M 28,72 L 12,78 L 8,88 L 22,96",                   dur: 5.0, color: "magenta" },
  // Right temple → out
  { id: "c3", d: "M 72,22 L 86,18 L 94,28 L 96,46",                  dur: 4.6, color: "cyan"    },
  // Right jaw → out
  { id: "c4", d: "M 72,72 L 88,78 L 92,88 L 78,96",                  dur: 5.3, color: "magenta" },
  // Crown
  { id: "c5", d: "M 50,12 L 50,4 L 38,2",                            dur: 3.8, color: "cyan"    },
  { id: "c6", d: "M 50,12 L 62,2",                                   dur: 3.4, color: "cyan"    },
  // Chin
  { id: "c7", d: "M 50,86 L 50,94",                                  dur: 3.6, color: "magenta" },
  // Inner orbit traces
  { id: "c8", d: "M 36,40 L 22,40 L 18,52",                          dur: 4.4, color: "cyan"    },
  { id: "c9", d: "M 64,40 L 78,40 L 82,52",                          dur: 4.4, color: "cyan"    },
];

const COLORS = {
  cyan:    "#00F0FF",
  magenta: "#E040FB",
};

// Node positions (dots at circuit intersections that "breathe")
const NODES = [
  { x: 28, y: 22 }, { x: 72, y: 22 }, { x: 28, y: 72 }, { x: 72, y: 72 },
  { x: 50, y: 12 }, { x: 50, y: 86 }, { x: 36, y: 40 }, { x: 64, y: 40 },
  { x: 50, y: 50 }, { x: 14, y: 18 }, { x: 86, y: 18 }, { x: 12, y: 78 },
  { x: 88, y: 78 },
];

export default function NeuralFace({ size = 360, className = "" }) {
  return (
    <div
      className={"relative " + className}
      style={{ width: size, height: size, maxWidth: "100%" }}
      data-testid="neural-face"
      aria-hidden="true"
    >
      <svg viewBox="0 0 100 100" width="100%" height="100%" style={{ overflow: "visible" }}>
        <defs>
          <filter id="nf-glow-cyan" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="0.7" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <filter id="nf-glow-magenta" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="0.7" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <radialGradient id="nf-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="rgba(0,240,255,0.55)" />
            <stop offset="60%" stopColor="rgba(0,240,255,0.18)" />
            <stop offset="100%" stopColor="rgba(0,240,255,0)" />
          </radialGradient>
        </defs>

        {/* Soft glow halo behind the face */}
        <circle cx="50" cy="50" r="46" fill="url(#nf-core)">
          <animate attributeName="r" values="44;48;44" dur="3.6s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.65;1;0.65" dur="3.6s" repeatCount="indefinite" />
        </circle>

        {/* Face outline + features (traces) */}
        {FACE_PATHS.map(p => (
          <path
            key={p.id}
            d={p.d}
            stroke="rgba(0,240,255,0.85)"
            strokeWidth={p.w * 0.6}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="url(#nf-glow-cyan)"
          >
            <animate attributeName="stroke-opacity" values="0.7;1;0.7" dur="2.4s" repeatCount="indefinite" />
          </path>
        ))}

        {/* External circuit traces */}
        {CIRCUIT_PATHS.map(c => (
          <path
            key={c.id}
            d={c.d}
            stroke={c.color === "cyan" ? "rgba(0,240,255,0.45)" : "rgba(224,64,251,0.45)"}
            strokeWidth="0.45"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}

        {/* Breathing nodes */}
        {NODES.map((n, i) => (
          <circle key={i} cx={n.x} cy={n.y} r="0.9" fill={COLORS.cyan} filter="url(#nf-glow-cyan)">
            <animate
              attributeName="r"
              values="0.7;1.3;0.7"
              dur={`${1.8 + (i % 4) * 0.25}s`}
              repeatCount="indefinite"
              begin={`${(i * 0.18) % 2}s`}
            />
            <animate
              attributeName="opacity"
              values="0.6;1;0.6"
              dur={`${1.8 + (i % 4) * 0.25}s`}
              repeatCount="indefinite"
              begin={`${(i * 0.18) % 2}s`}
            />
          </circle>
        ))}

        {/* Energy pulses · cyan + magenta riding circuit paths */}
        {CIRCUIT_PATHS.map((c) => (
          <circle
            key={`pulse-${c.id}`}
            r="0.85"
            fill={COLORS[c.color]}
            filter={c.color === "cyan" ? "url(#nf-glow-cyan)" : "url(#nf-glow-magenta)"}
          >
            <animateMotion dur={`${c.dur}s`} repeatCount="indefinite" path={c.d} rotate="auto" />
            <animate
              attributeName="opacity"
              values="0;1;1;0"
              keyTimes="0;0.15;0.85;1"
              dur={`${c.dur}s`}
              repeatCount="indefinite"
            />
          </circle>
        ))}

        {/* Pulses traveling the face outline + features for extra movement */}
        {FACE_PATHS.map((p, i) => (
          <circle
            key={`fp-${p.id}`}
            r="0.65"
            fill={i % 2 === 0 ? COLORS.cyan : COLORS.magenta}
            filter={i % 2 === 0 ? "url(#nf-glow-cyan)" : "url(#nf-glow-magenta)"}
          >
            <animateMotion
              dur={`${3.4 + i * 0.4}s`}
              repeatCount="indefinite"
              path={p.d}
              rotate="auto"
              begin={`${i * 0.3}s`}
            />
            <animate
              attributeName="opacity"
              values="0;1;1;0"
              keyTimes="0;0.2;0.8;1"
              dur={`${3.4 + i * 0.4}s`}
              repeatCount="indefinite"
              begin={`${i * 0.3}s`}
            />
          </circle>
        ))}
      </svg>
    </div>
  );
}
